import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Button,
  Space,
  Input,
  List,
  Tag,
  Empty,
  Typography,
  Collapse,
  Radio,
  Modal,
  Form,
  Select,
  message,
  Popconfirm,
  Skeleton,
  Divider,
} from 'antd';
import {
  SafetyCertificateOutlined,
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  UndoOutlined,
  AppstoreOutlined,
  FilterOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { rolePermissionsApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { CardListSkeleton } from '../../components/SkeletonLoaders';
import { useAuth } from '../../contexts/AuthContext';
import {
  getModuleTitleFa,
  PERMISSION_LEVEL_LABELS_FA,
} from '../../constants/modulesPermissionsCatalog';

const { Text, Paragraph } = Typography;

const normalizeKey = (name) => String(name || '').replace(/_/g, ' ');

const PERMISSION_LABELS_FA = {
  ...PERMISSION_LEVEL_LABELS_FA,
  all: 'همه',
  none: 'هیچ‌کدام',
  added: 'ایجادشده توسط کاربر',
  owned: 'متعلق به کاربر',
  both: 'ایجادشده + متعلق به کاربر',
};

const parseAllowedPermissions = (raw) => {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Object.entries(parsed || {}).map(([key, value]) => ({
      key,
      value,
      label: PERMISSION_LABELS_FA[key] || normalizeKey(key),
      apiValue: value,
    }));
  } catch {
    return [];
  }
};

// تابع کمکی برای تبدیل permission_type_id به key رشته‌ای
const getPermissionTypeKey = (typeId, allowedPermissionsJson) => {
  if (typeId === undefined || typeId === null) return null;
  const options = parseAllowedPermissions(allowedPermissionsJson);
  const found = options.find(opt => Number(opt.apiValue) === Number(typeId));
  return found?.key || options[0]?.key;
};

const selectedTypeFromPermission = (permission, options) => {
  const keyType = permission?.permission_type || permission?.selected_permission_type;
  if (keyType != null && keyType !== '') {
    // اگر keyType عددی بود، آن را به key رشته‌ای تبدیل کن
    if (typeof keyType === 'number' || !isNaN(Number(keyType))) {
      const byId = options.find((op) => Number(op.apiValue) === Number(keyType));
      if (byId) return byId.key;
    }
    return keyType;
  }
  const typeId = permission?.permission_type_id;
  if (typeId != null) {
    const byId = options.find((op) => Number(op.apiValue) === Number(typeId));
    if (byId) return byId.key;
  }
  return options[0]?.key;
};

const normalizeModules = (res) =>
  res?.permissions ||
  res?.data?.permissions ||
  res?.$modulesData ||
  res?.modulesData ||
  res?.modules ||
  res?.data?.modules ||
  [];
const normalizeRoles = (res) => res?.roles || res?.data?.roles || [];
const normalizeCustomPermissionsPayload = (res) => {
  if (!res) return null;
  if (res?.data?.permissions || res?.data?.role) return res;
  if (res?.permissions || res?.role) return { status: res?.status || 'success', data: res };
  return res;
};

const RolePermissionsSettings = () => {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [searchRole, setSearchRole] = useState('');

  const [modules, setModules] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [updatingPermissionKey, setUpdatingPermissionKey] = useState('');
  const [resetting, setResetting] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createLoadingMeta, setCreateLoadingMeta] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createImportRoles, setCreateImportRoles] = useState([]);
  const [createForm] = Form.useForm();

  const [customOpen, setCustomOpen] = useState(false);
  const [customLoading, setCustomLoading] = useState(false);
  const [customData, setCustomData] = useState(null);
  const [customModuleId, setCustomModuleId] = useState(null);
  const [customUpdatingKey, setCustomUpdatingKey] = useState('');

  const selectedRole = useMemo(
    () => roles.find((r) => String(r.id) === String(selectedRoleId)) || null,
    [roles, selectedRoleId],
  );
  const editingRoleId = selectedRole?.id ?? selectedRoleId;
  const editorUserId = user?.id;

  const filteredRoles = useMemo(() => {
    const q = searchRole.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter((r) => {
      const hay = `${r.display_name || ''} ${r.name || ''} ${r.description || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [roles, searchRole]);

  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const res = await rolePermissionsApi.getIndex();
      const list = normalizeRoles(res);
      setRoles(list);
      if (list.length && !list.some((x) => String(x.id) === String(selectedRoleId))) {
        setSelectedRoleId(list[0].id);
      } else if (!list.length) {
        setSelectedRoleId(null);
      }
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت نقش‌ها');
    } finally {
      setRolesLoading(false);
    }
  }, [selectedRoleId]);

  const fetchPermissions = useCallback(async (roleId) => {
    if (!roleId) {
      setModules([]);
      return;
    }
    setPermissionsLoading(true);
    try {
      const res = await rolePermissionsApi.getPermissions(roleId);
      setModules(normalizeModules(res));
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت دسترسی‌ها');
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  useEffect(() => {
    fetchPermissions(selectedRoleId);
  }, [selectedRoleId, fetchPermissions]);

  const onChangePermissionType = async (permission, nextType) => {
    if (!editorUserId) return;
    const opKey = `${permission.id}-${nextType}`;
    setUpdatingPermissionKey(opKey);
    try {
      // تبدیل nextType ("all") به permission_type_id عددی
      const options = parseAllowedPermissions(permission.allowed_permissions);
      const selectedOption = options.find((op) => op.key === nextType);
      const permissionTypeId = selectedOption?.apiValue;
      
      await rolePermissionsApi.updatePermission(editorUserId, {
        permission_id: permission.id,
        permission_type: permissionTypeId, // عددی
        permission_customised: 1,
      });
      setModules((prev) =>
        prev.map((m) => ({
          ...m,
          permissions: (m.permissions || []).map((p) =>
            p.id === permission.id ? { ...p, selected_permission_type: nextType } : p,
          ),
        })),
      );
      message.success('دسترسی بروزرسانی شد');
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, createForm);
      else message.error(error?.message || 'خطا در ذخیره دسترسی');
    } finally {
      setUpdatingPermissionKey('');
    }
  };

  const onCreateRoleOpen = async () => {
    setCreateOpen(true);
    createForm.resetFields();
    createForm.setFieldsValue({ name: '', import_from_role: undefined });
    setCreateLoadingMeta(true);
    try {
      const res = await rolePermissionsApi.getCreateMeta();
      setCreateImportRoles(normalizeRoles(res));
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت داده‌های ایجاد نقش');
    } finally {
      setCreateLoadingMeta(false);
    }
  };

  const onSubmitCreateRole = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateSubmitting(true);
      await rolePermissionsApi.createRole({
        name: String(values.name || '').trim(),
        import_from_role: values.import_from_role,
      });
      message.success('نقش جدید ایجاد شد');
      setCreateOpen(false);
      fetchRoles();
    } catch (error) {
      if (error?.errorFields) return;
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, createForm);
      else message.error(error?.message || 'خطا در ایجاد نقش');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const onDeleteRole = async (roleId) => {
    try {
      await rolePermissionsApi.deleteRole(roleId);
      message.success('نقش حذف شد');
      fetchRoles();
    } catch (error) {
      message.error(error?.message || 'خطا در حذف نقش');
    }
  };

  const onResetPermissions = async () => {
    if (!editorUserId) return;
    setResetting(true);
    try {
      await rolePermissionsApi.resetPermissions(selectedRoleId);
      message.success('دسترسی‌ها به حالت پیش‌فرض بازنشانی شد');
      fetchPermissions(selectedRoleId);
    } catch (error) {
      message.error(error?.message || 'خطا در بازنشانی دسترسی‌ها');
    } finally {
      setResetting(false);
    }
  };

  const onOpenCustomPermissions = async (moduleId) => {
    if (!selectedRoleId) return;
    setCustomOpen(true);
    setCustomModuleId(moduleId);
    setCustomLoading(true);
    setCustomData(null);
    try {
      const res = await rolePermissionsApi.getCustomPermissions({
        module_id: moduleId,
        role_id: selectedRoleId,
      });
      setCustomData(normalizeCustomPermissionsPayload(res));
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت دسترسی‌های سفارشی');
    } finally {
      setCustomLoading(false);
    }
  };

  const onChangeCustomPermissionType = async (permission, nextType) => {
    if (!selectedRoleId || !editorUserId) return;
    const opKey = `custom-${permission.id}-${nextType}`;
    setCustomUpdatingKey(opKey);
    try {
      // تبدیل nextType ("all") به permission_type_id عددی
      const options = parseAllowedPermissions(permission.allowed_permissions);
      const selectedOption = options.find((op) => op.key === nextType);
      const permissionTypeId = selectedOption?.apiValue;
      
      // ارسال پیلود با permission_type عددی
      await rolePermissionsApi.updatePermission(editorUserId, {
        permission_id: permission.id,
        permission_type: permissionTypeId, // عددی
        permission_customised: 1,
      });
      
      // آپدیت state محلی
      setCustomData((prev) => {
        if (!prev) return prev;
        const next = JSON.parse(JSON.stringify(prev));
        const rolePerms = next?.data?.role?.permissions;
        if (Array.isArray(rolePerms)) {
          const existingIndex = rolePerms.findIndex(
            (rp) => Number(rp.permission_id) === Number(permission.id)
          );
          
          if (existingIndex >= 0) {
            rolePerms[existingIndex].permission_type_id = permissionTypeId;
          } else {
            rolePerms.push({
              permission_id: permission.id,
              role_id: editingRoleId,
              permission_type_id: permissionTypeId,
            });
          }
        }
        return next;
      });
      
      message.success('دسترسی سفارشی بروزرسانی شد');
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, createForm);
      else message.error(error?.message || 'خطا در ذخیره دسترسی سفارشی');
    } finally {
      setCustomUpdatingKey('');
    }
  };

  const customRolePermissionMap = useMemo(() => {
    const rolePermissions = customData?.data?.role?.permissions || [];
    const map = new Map();
    rolePermissions.forEach((row) => {
      map.set(Number(row.permission_id), Number(row.permission_type_id));
    });
    return map;
  }, [customData]);

  return (
    <MainLayout>
      <div className="custom-font">
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-700/70">
                <SafetyCertificateOutlined className="text-xl text-gray-700 dark:text-gray-300" />
              </div>
              <div>
                <h1 className="m-0 text-2xl font-bold text-gray-800 dark:text-gray-100">نقش‌ها و دسترسی‌ها</h1>
                <Text type="secondary">مدیریت دقیق سطوح دسترسی کاربران به ماژول‌های سیستم</Text>
              </div>
            </div>
            <Space wrap>
              <Button icon={<ReloadOutlined />} loading={rolesLoading} onClick={fetchRoles}>
                بروزرسانی
              </Button>
              <Button type="primary" icon={<PlusOutlined />} className={PANEL_PRIMARY_BTN_CLASS} onClick={onCreateRoleOpen}>
                نقش جدید
              </Button>
            </Space>
          </div>

          <div className="bg-gray-50 p-6 dark:bg-gray-900/50">
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[340px_minmax(0,1fr)]">
              <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800" styles={{ body: { padding: 18 } }}>
                <Input
                  allowClear
                  placeholder="جستجوی نقش..."
                  prefix={<FilterOutlined />}
                  value={searchRole}
                  onChange={(e) => setSearchRole(e.target.value)}
                  className="mb-3"
                />
                {rolesLoading ? (
                  <CardListSkeleton count={4} />
                ) : filteredRoles.length ? (
                  <List
                    split={false}
                    dataSource={filteredRoles}
                    renderItem={(role) => {
                      const active = String(role.id) === String(selectedRoleId);
                      return (
                        <List.Item
                          key={role.id}
                          onClick={() => setSelectedRoleId(role.id)}
                          className="!mb-2 !border-0 !p-0"
                        >
                          <div className={`cursor-pointer rounded-2xl border px-5 py-3 transition-all duration-200 w-full ${
                            active
                              ? 'border-blue-400 bg-gradient-to-r from-blue-50 to-indigo-50 shadow-sm dark:border-blue-500 dark:from-blue-900/25 dark:to-indigo-900/20'
                              : 'border-gray-100 bg-white hover:-translate-y-[1px] hover:border-gray-200 hover:shadow-sm dark:border-gray-700 dark:bg-gray-800/70 dark:hover:bg-gray-700/35'
                          }`}>
                            <div className="w-full px-1">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className={`inline-block h-2.5 w-2.5 rounded-full ${active ? 'bg-blue-500' : 'bg-gray-300'}`} />
                                    <Text strong className="truncate">{role.display_name || role.name}</Text>
                                  </div>
                                  <Paragraph type="secondary" className="!m-0 !mt-1 text-xs" ellipsis={{ rows: 1 }}>
                                    {role.name}
                                  </Paragraph>
                                </div>
                                <Tag color={active ? 'geekblue' : 'blue'} className="m-0 rounded-full px-2">
                                  {role.users_count ?? role.unsynced_users_count ?? 0} کاربر
                                </Tag>
                              </div>
                              {role.description ? (
                                <Paragraph className="!m-0 !mt-2 text-xs text-gray-500" ellipsis={{ rows: 2 }}>
                                  {role.description}
                                </Paragraph>
                              ) : null}
                            </div>
                          </div>
                        </List.Item>
                      );
                    }}
                  />
                ) : (
                  <Empty description="نقشی یافت نشد" />
                )}
              </Card>

              <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800" styles={{ body: { padding: 18 } }}>
                {selectedRole ? (
                  <>
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 bg-white p-3 dark:border-gray-700 dark:bg-gray-800/80">
                      <div className="min-w-0">
                        <Text strong className="text-base">{selectedRole.display_name || selectedRole.name}</Text>
                        <div className="mt-1 text-xs text-gray-500">{selectedRole.description || 'بدون توضیح'}</div>
                      </div>
                      <Space wrap>
                        <Button icon={<UndoOutlined />} onClick={onResetPermissions} loading={resetting}>
                          ریست دسترسی‌ها
                        </Button>
                        <Popconfirm
                          title="حذف این نقش؟"
                          description="این عملیات قابل بازگشت نیست."
                          okText="حذف"
                          cancelText="لغو"
                          onConfirm={() => onDeleteRole(selectedRole.id)}
                        >
                          <Button danger icon={<DeleteOutlined />}>حذف نقش</Button>
                        </Popconfirm>
                      </Space>
                    </div>

                    {permissionsLoading ? (
                      <div className="space-y-3">
                        <Skeleton active paragraph={{ rows: 6 }} />
                        <Skeleton active paragraph={{ rows: 6 }} />
                      </div>
                    ) : modules.length ? (
                      <Collapse
                        size="large"
                        defaultActiveKey={modules.slice(0, 4).map((m) => String(m.id))}
                        items={modules.map((module) => ({
                          key: String(module.id),
                          label: (
                            <div className="flex items-center gap-2">
                              <AppstoreOutlined />
                              <span>{getModuleTitleFa(module.module_name) || normalizeKey(module.module_name)}</span>
                              <Tag className="m-0">{(module.permissions || []).length} دسترسی</Tag>
                              {module.custom_permissions_count ? <Tag color="purple" className="m-0">سفارشی: {module.custom_permissions_count}</Tag> : null}
                            </div>
                          ),
                          children: (
                            <div className="space-y-3">
                              {module.description ? (
                                <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-gray-700/35">{module.description}</div>
                              ) : null}
                              {(module.permissions || []).length ? (
                                <div className="space-y-2">
                                  {(module.permissions || []).map((permission) => {
                                    const options = parseAllowedPermissions(permission.allowed_permissions);
                                    const defaultType = selectedTypeFromPermission(permission, options);
                                    return (
                                      <div key={permission.id} className="rounded-lg border border-gray-100 p-3 dark:border-gray-700">
                                        <div className="mb-2 flex items-center justify-between gap-2">
                                          <div>
                                            <Text strong>{permission.display_name || permission.name}</Text>
                                            <div className="text-xs text-gray-500">{permission.name}</div>
                                          </div>
                                          <Tag color={permission.is_custom ? 'purple' : 'default'}>{permission.is_custom ? 'سفارشی' : 'سیستمی'}</Tag>
                                        </div>
                                        <Radio.Group
                                          value={defaultType}
                                          onChange={(e) => onChangePermissionType(permission, e.target.value)}
                                          optionType="button"
                                          buttonStyle="solid"
                                          className="w-full"
                                        >
                                          <Space wrap>
                                            {options.map((op) => (
                                              <Radio.Button
                                                key={op.key}
                                                value={op.key}
                                                disabled={updatingPermissionKey === `${permission.id}-${op.key}`}
                                              >
                                                {op.label}
                                              </Radio.Button>
                                            ))}
                                          </Space>
                                        </Radio.Group>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <Empty description="دسترسی مستقیمی برای این ماژول تعریف نشده است." />
                              )}

                              <Divider className="!my-3" />
                              <Button icon={<SettingOutlined />} onClick={() => onOpenCustomPermissions(module.id)}>
                                مشاهده دسترسی‌های سفارشی ماژول
                              </Button>
                            </div>
                          ),
                        }))}
                      />
                    ) : (
                      <Empty description="اطلاعات دسترسی برای این نقش یافت نشد" />
                    )}
                  </>
                ) : (
                  <Empty description="یک نقش را از ستون سمت راست انتخاب کنید" />
                )}
              </Card>
            </div>
          </div>
        </Card>

        <Modal
          title="ایجاد نقش جدید"
          open={createOpen}
          onCancel={() => setCreateOpen(false)}
          onOk={onSubmitCreateRole}
          okText="ایجاد نقش"
          cancelText="لغو"
          confirmLoading={createSubmitting}
          okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
          width={560}
          destroyOnClose
          className="custom-font"
        >
          {createLoadingMeta ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <Form form={createForm} layout="vertical" className="custom-font mt-3">
              <Form.Item
                name="name"
                label="نام نقش"
                rules={[{ required: true, message: 'نام نقش را وارد کنید' }]}
                extra="نام سیستمی نقش (مثال: sales_manager)"
              >
                <Input dir="ltr" />
              </Form.Item>
              <Form.Item
                name="import_from_role"
                label="کپی دسترسی‌ها از نقش"
                rules={[{ required: true, message: 'یک نقش را برای کپی انتخاب کنید' }]}
              >
                <Select
                  placeholder="یک نقش انتخاب کنید"
                  options={createImportRoles.map((r) => ({
                    value: r.id,
                    label: `${r.display_name || r.name} (${r.name})`,
                  }))}
                  showSearch
                  optionFilterProp="label"
                  popupClassName="custom-font"
                />
              </Form.Item>
            </Form>
          )}
        </Modal>

        <Modal
          title={`دسترسی‌های سفارشی ماژول ${customModuleId || ''}`}
          open={customOpen}
          onCancel={() => setCustomOpen(false)}
          footer={null}
          width={780}
          className="custom-font"
          destroyOnClose
        >
          {customLoading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : (customData?.data?.permissions?.custom_permissions || []).length ? (
            <div className="max-h-[65vh] space-y-3 overflow-auto pr-1">
              {(customData?.data?.permissions?.custom_permissions || []).map((permission) => {
                const options = parseAllowedPermissions(permission.allowed_permissions);
                const currentTypeId = customRolePermissionMap.get(Number(permission.id));
                const selectedType = getPermissionTypeKey(currentTypeId, permission.allowed_permissions);
                return (
                  <div key={permission.id} className="rounded-xl border border-gray-100 bg-white p-3 dark:border-gray-700 dark:bg-gray-800/60">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <Text strong>{permission.display_name || permission.name}</Text>
                        <div className="text-xs text-gray-500">{permission.name}</div>
                      </div>
                      <Tag color="purple">سفارشی</Tag>
                    </div>
                    <Radio.Group
                      value={selectedType}
                      onChange={(e) => onChangeCustomPermissionType(permission, e.target.value)}
                      optionType="button"
                      buttonStyle="solid"
                    >
                      <Space wrap>
                        {options.map((op) => (
                          <Radio.Button
                            key={op.key}
                            value={op.key}
                            disabled={customUpdatingKey === `custom-${permission.id}-${op.key}`}
                          >
                            {op.label}
                          </Radio.Button>
                        ))}
                      </Space>
                    </Radio.Group>
                  </div>
                );
              })}
            </div>
          ) : (
            <Empty description="داده‌ای برای نمایش وجود ندارد" />
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default RolePermissionsSettings;